import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { renderMarkdown } from '../../markdown';
import { t } from '../../i18n';
import { icon } from './icons';

const escText = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escAttr = (s: string) => escText(s).replace(/"/g, '&quot;');

// --- DOM → Markdown serialization (the limited subset this editor produces) ---
// The editor renders Markdown to HTML for editing (renderMarkdown), the user edits
// visually, and we walk the resulting DOM back to Markdown on every change so the stored
// value stays Markdown. styleWithCSS is forced off so formatting uses <b>/<i> tags
// (not styled spans), keeping this serializer simple and reliable.

function mdInline(node: Node): string {
  let out = '';
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      out += (child.textContent ?? '').replace(/\n/g, ' ');
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const el = child as HTMLElement;
    const inner = mdInline(el);
    switch (el.tagName) {
      case 'STRONG': case 'B': out += inner.trim() ? `**${inner}**` : inner; break;
      case 'EM': case 'I': out += inner.trim() ? `_${inner}_` : inner; break;
      case 'CODE': out += inner.trim() ? `\`${inner}\`` : inner; break;
      case 'A': {
        const href = el.getAttribute('href') || '';
        out += href ? `[${inner}](${href})` : inner;
        break;
      }
      case 'BR': out += '\n'; break;
      default: out += inner;
    }
  });
  return out;
}

function mdBlocks(root: Element): string {
  const parts: string[] = [];
  const push = (s: string) => { const t = s.trim(); if (t) parts.push(t); };
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) { push(node.textContent ?? ''); return; }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    // Empty blocks (e.g. <div><br></div> from pressing Enter) must not emit a marker like
    // "##" — only emit when there's actual text.
    const inner = mdInline(el).trim();
    switch (el.tagName) {
      case 'H1': if (inner) push('# ' + inner); break;
      case 'H2': if (inner) push('## ' + inner); break;
      case 'H3': if (inner) push('### ' + inner); break;
      case 'H4': case 'H5': case 'H6': if (inner) push('#### ' + inner); break;
      case 'UL':
        push(Array.from(el.children).filter((c) => c.tagName === 'LI').map((li) => '- ' + mdInline(li).trim()).join('\n'));
        break;
      case 'OL':
        push(Array.from(el.children).filter((c) => c.tagName === 'LI').map((li, i) => `${i + 1}. ` + mdInline(li).trim()).join('\n'));
        break;
      case 'BLOCKQUOTE':
        if (inner) push(inner.split('\n').map((l) => '> ' + l).join('\n'));
        break;
      case 'PRE': push('```\n' + (el.textContent ?? '').replace(/\n$/, '') + '\n```'); break;
      case 'BR': break;
      default: if (inner) push(inner); // P, DIV, and anything else collapse to a paragraph
    }
  });
  return parts.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
}

const BLOCKS = [
  { value: 'p', key: 'ui.editor.normalText' },
  { value: 'h1', key: 'ui.editor.heading1' },
  { value: 'h2', key: 'ui.editor.heading2' },
  { value: 'h3', key: 'ui.editor.heading3' },
] as const;

/**
 * <cadence-rich-text-editor .value=${md} placeholder="…"> — a visual (WYSIWYG) editor
 * for a limited Markdown subset: bold, italic, headings, bullet lists, links, and inline
 * code. It edits a contenteditable surface and serializes the DOM back to Markdown on
 * every change, so the value in/out is always a Markdown string (`input` event, string in
 * `e.detail`). Dependency-free (no WYSIWYG library), per the project's "Lit only, native
 * APIs" rule. Reused for notes, contracts, and emails.
 */
@customElement('cadence-rich-text-editor')
export class CadenceRichTextEditor extends LitElement {
  @property() value = '';
  @property() placeholder = 'Start typing…';
  @property({ type: Number, attribute: 'min-height' }) minHeight = 220;
  @state() private fmt = { bold: false, italic: false, ul: false };
  @state() private block = 'p';
  @state() private menuOpen = false;
  @query('.editor') private editor!: HTMLDivElement;

  /** Last Markdown we serialized — lets us tell our own edits from external value changes. */
  private last = '';
  /** Caret range inside the editor, captured so toolbar controls that steal focus
   *  (notably the native <select>) can restore it before running a command. */
  private savedRange: Range | null = null;

  static styles = css`
    :host { display: block; }
    .wrap { border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); overflow: hidden; }
    .bar { display: flex; align-items: center; gap: 2px; padding: 4px 6px; border-bottom: 1px solid var(--color-border); background: var(--color-bg-secondary); flex-wrap: wrap; }
    .blockmenu { position: relative; margin-right: 4px; }
    .blockmenu .trigger { display: inline-flex; align-items: center; gap: 4px; font: inherit; font-size: 0.8rem; border: 1px solid var(--color-border); border-radius: 6px; background: var(--color-bg-primary); color: var(--color-text-primary); padding: 4px 8px; cursor: pointer; min-width: 110px; justify-content: space-between; }
    .blockmenu .trigger:hover { border-color: var(--color-primary); }
    .blockmenu .trigger svg { width: 14px; height: 14px; flex: none; }
    .blockmenu .menu { position: absolute; top: calc(100% + 4px); left: 0; z-index: 20; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 8px; box-shadow: var(--shadow-md, 0 6px 18px rgba(0,0,0,0.18)); padding: 4px; min-width: 150px; display: flex; flex-direction: column; gap: 2px; }
    .blockmenu .menu button { text-align: left; font: inherit; font-size: 0.85rem; border: none; background: none; color: var(--color-text-primary); padding: 6px 10px; border-radius: 6px; cursor: pointer; }
    .blockmenu .menu button:hover { background: var(--color-bg-secondary); }
    .blockmenu .menu button.active { color: var(--color-primary); font-weight: 600; }
    .sep { width: 1px; align-self: stretch; background: var(--color-border); margin: 4px 4px; }
    .tbtn { background: none; border: none; cursor: pointer; color: var(--color-text-secondary); padding: 5px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; }
    .tbtn:hover { background: var(--color-bg-primary); color: var(--color-primary); }
    .tbtn.on { background: var(--color-primary); color: #fff; }
    .tbtn svg { width: 17px; height: 17px; }

    .editor { padding: 12px 14px; outline: none; overflow-y: auto; color: var(--color-text-primary); }
    .editor:empty::before { content: attr(data-placeholder); color: var(--color-text-secondary); pointer-events: none; }
    .editor :first-child { margin-top: 0; }
    .editor :last-child { margin-bottom: 0; }
    .editor h1, .editor h2, .editor h3 { line-height: 1.25; margin: 14px 0 6px; }
    .editor h1 { font-size: 1.35rem; }
    .editor h2 { font-size: 1.18rem; }
    .editor h3 { font-size: 1.05rem; }
    .editor p, .editor ul, .editor ol { margin: 8px 0; }
    .editor ul, .editor ol { padding-left: 22px; }
    .editor code { background: var(--color-bg-secondary); padding: 1px 4px; border-radius: 4px; font-family: var(--font-mono, ui-monospace, monospace); font-size: 0.9em; }
    .editor pre { background: var(--color-bg-secondary); padding: 10px; border-radius: 6px; overflow: auto; }
    .editor pre code { padding: 0; background: none; }
    .editor blockquote { margin: 8px 0; padding-left: 12px; border-left: 3px solid var(--color-border); color: var(--color-text-secondary); }
    .editor a { color: var(--color-primary); }
  `;

  /** Close the block menu when clicking anywhere outside this component. */
  private onDocPointerDown = (e: Event) => {
    if (this.menuOpen && !e.composedPath().includes(this)) this.menuOpen = false;
  };

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('pointerdown', this.onDocPointerDown, true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.onDocPointerDown, true);
  }

  firstUpdated() {
    try { document.execCommand('styleWithCSS', false, 'false'); } catch { /* unsupported */ }
    // Enter should create <p> blocks (not <div>), keeping the serializer output clean.
    try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch { /* unsupported */ }
    this.last = this.value;
    if (this.editor) this.editor.innerHTML = this.value ? renderMarkdown(this.value) : '';
  }

  updated(changed: Map<string, unknown>) {
    // Only reset the DOM when the value changed from the *outside* — never on our own
    // edits (which would wipe the caret on every keystroke).
    if (changed.has('value') && this.value !== this.last && this.editor) {
      this.last = this.value;
      this.editor.innerHTML = this.value ? renderMarkdown(this.value) : '';
    }
  }

  /** Selection access that works inside Shadow DOM (Chromium) with a document fallback. */
  private getSelection(): Selection | null {
    const root = this.shadowRoot as unknown as { getSelection?: () => Selection };
    return typeof root?.getSelection === 'function' ? root.getSelection() : document.getSelection();
  }

  /** Remember the caret range while it's inside the editor. */
  private saveRange() {
    const sel = this.getSelection();
    if (sel && sel.rangeCount && this.editor && this.editor.contains(sel.anchorNode)) {
      this.savedRange = sel.getRangeAt(0).cloneRange();
    }
  }

  /** Put the saved caret range back (after a toolbar control stole focus). */
  private restoreRange() {
    const sel = this.getSelection();
    const r = this.savedRange;
    if (!sel || !r || !this.editor) return;
    if (!this.editor.contains(r.startContainer)) return; // stale / foreign range
    sel.removeAllRanges();
    try { sel.addRange(r); } catch { /* detached range */ }
  }

  private serialize() {
    const md = this.editor ? mdBlocks(this.editor) : '';
    this.last = md;
    this.value = md;
    this.dispatchEvent(new CustomEvent('input', { detail: md, bubbles: true, composed: true }));
  }

  private onEditorInput(e: Event) {
    // Stop the native (composed) input event at the shadow boundary so the host sees only
    // our CustomEvent carrying the Markdown string in `detail`.
    e.stopPropagation();
    this.serialize();
    this.saveRange();
    this.syncToolbar();
  }

  /** Caret moved within the editor (keyup/mouseup) — track it and refresh the toolbar. */
  private onCaret() {
    this.saveRange();
    this.syncToolbar();
  }

  /** Pick a block style from the custom menu. Focus never left the editor (the menu
   *  buttons preventDefault on mousedown), so the live selection is still valid. */
  private pickBlock(tag: string) {
    this.menuOpen = false;
    this.setBlock(tag);
  }

  private exec(cmd: string, val?: string) {
    this.menuOpen = false;
    this.editor?.focus();
    this.restoreRange();
    try { document.execCommand(cmd, false, val); } catch { /* command unsupported */ }
    this.serialize();
    this.saveRange();
    this.syncToolbar();
  }

  /**
   * Change the block type of every line the selection touches. Done with a direct DOM
   * transform (not execCommand('formatBlock'), whose caret handling across a focus change
   * is unreliable and wiped content). Handles three top-level node shapes the browser
   * produces: real block elements (re-tagged), and bare text/inline runs that the browser
   * leaves unwrapped (grouped into a new block). Lists are left untouched. Content is only
   * ever moved or re-tagged — never deleted.
   */
  private setBlock(tag: string) {
    if (!this.editor) return;
    const ed = this.editor;
    ed.focus();
    this.restoreRange();
    const sel = this.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const BLOCK = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE']);
    const LIST = new Set(['UL', 'OL', 'LI']);

    let last: HTMLElement | null = null;
    let run: Node[] = []; // contiguous bare inline/text nodes forming one visual line
    const flushRun = () => {
      if (!run.length) return;
      const created = document.createElement(tag);
      ed.insertBefore(created, run[0]);
      run.forEach((n) => created.appendChild(n));
      last = created;
      run = [];
    };

    for (const node of Array.from(ed.childNodes)) {
      const isEl = node.nodeType === Node.ELEMENT_NODE;
      const tn = isEl ? (node as HTMLElement).tagName : '';
      if (isEl && LIST.has(tn)) { flushRun(); continue; } // leave lists alone
      if (isEl && BLOCK.has(tn)) {
        flushRun();
        if (range.intersectsNode(node)) { const c = this.convertBlock(node as HTMLElement, tag); if (c) last = c; }
        continue;
      }
      if (node.nodeName === 'BR') { flushRun(); continue; } // a stray <br> ends a line
      // Bare text or inline node (text, span, b, i, a, code).
      if (range.intersectsNode(node)) run.push(node);
      else flushRun();
    }
    flushRun();

    if (last) {
      const r = document.createRange();
      r.selectNodeContents(last);
      r.collapse(false); // caret at end of the converted block
      sel.removeAllRanges();
      sel.addRange(r);
    }
    this.serialize();
    this.saveRange();
    this.syncToolbar();
  }

  /** Re-tag a block element, moving its children into the new element. Lists are skipped. */
  private convertBlock(el: HTMLElement, tag: string): HTMLElement | null {
    const cur = el.tagName.toLowerCase();
    if (['ul', 'ol', 'li'].includes(cur)) return null;
    if (cur === tag) return el;
    const created = document.createElement(tag);
    while (el.firstChild) created.appendChild(el.firstChild);
    el.replaceWith(created);
    return created;
  }

  private link() {
    const url = prompt(t('ui.editor.linkPrompt'), 'https://')?.trim();
    if (!url) return;
    const sel = this.getSelection();
    if (sel && !sel.isCollapsed) this.exec('createLink', url);
    else this.exec('insertHTML', `<a href="${escAttr(url)}">${escText(url)}</a>`);
  }

  private code() {
    const text = this.getSelection()?.toString() ?? '';
    this.exec('insertHTML', `<code>${escText(text || 'code')}</code>`);
  }

  /** Reflect the caret's current formatting into the toolbar (active states + block menu). */
  private syncToolbar() {
    try {
      this.fmt = {
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        ul: document.queryCommandState('insertUnorderedList'),
      };
    } catch { /* queryCommandState unsupported */ }
    this.block = this.currentBlock();
    // NB: do NOT saveRange() here — syncToolbar runs on focus(), and saving the
    // post-focus selection would clobber the range we need to restore.
  }

  private currentBlock(): string {
    const node = this.getSelection()?.anchorNode ?? null;
    const editor = this.editor;
    if (!node || !editor) return 'p';
    let el: HTMLElement | null = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
    while (el && el !== editor && el.parentElement !== editor) el = el.parentElement;
    if (!el || el === editor) return 'p';
    const tag = el.tagName.toLowerCase();
    return ['h1', 'h2', 'h3'].includes(tag) ? tag : 'p';
  }

  render() {
    // Keep the editor's selection alive when an icon button is pressed (so formatting
    // applies to the current selection). The <select> must NOT preventDefault, or its
    // native dropdown won't open — it restores the caret via the saved range instead.
    const noMouse = (e: Event) => e.preventDefault();
    const currentLabel = t((BLOCKS.find((b) => b.value === this.block) ?? BLOCKS[0]).key);
    return html`
      <div class="wrap">
        <div class="bar">
          <div class="blockmenu">
            <button type="button" class="trigger" title=${t('ui.editor.textStyle')} aria-haspopup="listbox" aria-expanded=${this.menuOpen}
              @mousedown=${noMouse} @click=${() => (this.menuOpen = !this.menuOpen)}>
              <span>${currentLabel}</span>${icon('chevron-down')}
            </button>
            ${this.menuOpen ? html`<div class="menu" role="listbox">
              ${BLOCKS.map((b) => html`<button type="button" role="option" aria-selected=${this.block === b.value}
                class=${this.block === b.value ? 'active' : ''} @mousedown=${noMouse} @click=${() => this.pickBlock(b.value)}>${t(b.key)}</button>`)}
            </div>` : ''}
          </div>
          <span class="sep"></span>
          <button type="button" class="tbtn ${this.fmt.bold ? 'on' : ''}" title=${t('ui.editor.bold')} @mousedown=${noMouse} @click=${() => this.exec('bold')}>${icon('bold')}</button>
          <button type="button" class="tbtn ${this.fmt.italic ? 'on' : ''}" title=${t('ui.editor.italic')} @mousedown=${noMouse} @click=${() => this.exec('italic')}>${icon('italic')}</button>
          <button type="button" class="tbtn ${this.fmt.ul ? 'on' : ''}" title=${t('ui.editor.bulletList')} @mousedown=${noMouse} @click=${() => this.exec('insertUnorderedList')}>${icon('list')}</button>
          <button type="button" class="tbtn" title=${t('ui.editor.link')} @mousedown=${noMouse} @click=${() => this.link()}>${icon('link')}</button>
          <button type="button" class="tbtn" title=${t('ui.editor.inlineCode')} @mousedown=${noMouse} @click=${() => this.code()}>${icon('code')}</button>
        </div>
        <div
          class="editor"
          contenteditable="true"
          role="textbox"
          aria-multiline="true"
          data-placeholder=${this.placeholder}
          style="min-height:${this.minHeight}px"
          @input=${this.onEditorInput}
          @keyup=${this.onCaret}
          @mouseup=${this.onCaret}
          @focus=${this.syncToolbar}></div>
      </div>
    `;
  }
}
